import { Add, DeleteOutline, Remove, ShoppingCart } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "./Header.js";
import MultiStepCheckoutForm from "./MultiStepCheckoutForm.js";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const EXCHANGE_RATE_INR_TO_CNY = 0.083;

function CartPage() {
  const [cart, setCart] = useState({ items: [] });
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const navigate = useNavigate();
  const userEmail = localStorage.getItem("userEmail");
  const [userId, setUserId] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem("selectedLanguage") || "en");

  const fetchUserId = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/getUserId/${userEmail}`);
      setUserId(response.data.userId);
    } catch (err) {
      toast.error("Failed to fetch user ID.");
      console.error("Error fetching user ID:", err);
    }
  }, [userEmail]);

  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cart/${userId}`);
      const cartData = response.data || { items: [] };
      setCart({ ...cartData, items: cartData.items || [] });
    } catch (err) {
      console.error("Error fetching cart:", err);
      toast.error("Failed to load cart.");
    }
  }, [userId]);

  useEffect(() => {
    if (!userEmail) {
      toast.error("User not logged in!");
      navigate("/login");
      return;
    }
    fetchUserId();

    // Listen for language changes in localStorage
    const handleStorageChange = () => {
      setSelectedLanguage(localStorage.getItem("selectedLanguage") || "en");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [userEmail, navigate, fetchUserId]);

  useEffect(() => {
    if (userId) {
      fetchCart();
    }
  }, [userId, fetchCart]);

  const removeFromCart = async (productId) => {
    if (!productId) return;
    try {
      await axios.delete(`${API_BASE_URL}/cart/${userId}/item/${productId}`);
      setCart((prevCart) => ({
        ...prevCart,
        items: prevCart.items.filter((item) => item?.productId?._id !== productId),
      }));
      toast.success("Item removed from cart!");
    } catch (err) {
      toast.error("Failed to remove item.");
      console.error("Error removing item:", err);
    }
    window.location.reload();
  };

  const updateQuantity = async (productId, change) => {
    if (!productId) return;
    const item = cart.items.find((item) => item?.productId?._id === productId);
    if (!item) return;

    const newQuantity = Math.max(1, item.quantity + change);

    try {
      await axios.patch(`${API_BASE_URL}/cart/${userId}/item/${productId}`, {
        quantity: newQuantity,
      });
      setCart((prevCart) => ({
        ...prevCart,
        items: prevCart.items.map((item) =>
          item?.productId?._id === productId ? { ...item, quantity: newQuantity } : item
        ),
      }));
      window.location.reload();
    } catch (err) {
      toast.error("Failed to update quantity.");
      console.error("Error updating quantity:", err);
    }
  };

  const getPriceDetails = (price, discountPrice) => {
    const isChinese = selectedLanguage === "zh-TW";
    const currencySymbol = isChinese ? "¥" : "₹";
    const conversionRate = isChinese ? EXCHANGE_RATE_INR_TO_CNY : 1;

    const convertedPrice = (price * conversionRate).toFixed(2);
    const convertedDiscountPrice = (discountPrice * conversionRate).toFixed(2);
    const finalPrice = (price - discountPrice) * conversionRate;

    return {
      symbol: currencySymbol,
      finalPrice: finalPrice.toFixed(2),
      originalPrice: convertedPrice,
      discount: convertedDiscountPrice,
    };
  };

  const getTotalPrice = () => {
    return cart.items
      .reduce((total, item) => {
        if (!item?.productId?.price || !item?.productId?.discountPrice) return total;
        const { finalPrice } = getPriceDetails(item.productId.price, item.productId.discountPrice);
        return total + parseFloat(finalPrice) * (item.quantity || 1);
      }, 0)
      .toFixed(2);
  };

  const handleProceedToBuy = () => {
    setIsCheckoutOpen(true);
  };

  return (
    <>
      <Header />
      <div
        className="container-fuild"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "linear-gradient(45deg, #1a1a1a 0%, #2d2d2d 100%)",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 4, md: 5 },
            mt: { xs: 8, sm: 12, md: 15 },
            bgcolor: "background.paper",
            width: "85%",
            display: "flex",
            flexDirection: "column",
            marginBottom: "30px",
            gap: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: "primary.main",
              fontSize: { xs: "1.5rem", sm: "2rem" },
            }}
          >
            Your Cart
          </Typography>
          {cart.items.length === 0 ? (
            <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
              Your cart is empty
            </Typography>
          ) : (
            <Box>
              <List>
                {cart.items.map(
                  (item) =>
                    item?.productId && (
                      <React.Fragment key={item.productId._id}>
                        <ListItem
                          sx={{
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: "center",
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              alt={item.productId.model || "Product"}
                              src={`https://covercraft-x28n.onrender.com${item.productId.image}`}
                              variant="square"
                              sx={{
                                width: { xs: 80, sm: 100, md: 120 },
                                height: { xs: 100, sm: 140, md: 160 },
                                mb: { xs: 1, sm: 0 },
                                objectFit: "cover",
                                borderRadius: 1,
                              }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={item.productId.model || "Unknown Product"}
                            secondary={
                              <>
                                <div>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.primary"
                                  >
                                    {getPriceDetails(item.productId.price, item.productId.discountPrice).symbol}
                                    {getPriceDetails(item.productId.price, item.productId.discountPrice).finalPrice}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    style={{
                                      textDecoration: "line-through",
                                      marginLeft: "10px",
                                    }}
                                  >
                                    {getPriceDetails(item.productId.price, item.productId.discountPrice).symbol}
                                    {getPriceDetails(item.productId.price, item.productId.discountPrice).originalPrice}
                                  </Typography>
                                </div>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mt: 1,
                                  }}
                                >
                                  <IconButton
                                    onClick={() => updateQuantity(item.productId._id, -1)}
                                    disabled={item.quantity <= 1}
                                  >
                                    <Remove />
                                  </IconButton>
                                  <Typography sx={{ mx: 1 }}>
                                    {item.quantity || 1}
                                  </Typography>
                                  <IconButton
                                    onClick={() => updateQuantity(item.productId._id, 1)}
                                  >
                                    <Add />
                                  </IconButton>
                                </Box>
                              </>
                            }
                          />
                          <Button
                            startIcon={<DeleteOutline />}
                            onClick={() => removeFromCart(item.productId._id)}
                            color="error"
                          >
                            Remove
                          </Button>
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </React.Fragment>
                    )
                )}
              </List>
              <Box sx={{ mt: 3, textAlign: "right" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Total: {cart.items.length > 0 ? getPriceDetails(cart.items[0].productId.price, cart.items[0].productId.discountPrice).symbol : "₹"}{getTotalPrice()}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ShoppingCart />}
                  onClick={handleProceedToBuy}
                  size="large"
                  fullWidth
                >
                  Proceed to Buy
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
        <Dialog
          open={isCheckoutOpen}
          onClose={() => setIsCheckoutOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <MultiStepCheckoutForm
            totalPrice={getTotalPrice()}
            onClose={() => setIsCheckoutOpen(false)}
          />
        </Dialog>
      </div>
    </>
  );
}

export default CartPage;